import { SpatialMatrix3x3, SpatialPerson } from '../../../shared/models/be/spatial';

interface AvatarEllipse { cx: number; cy: number; rx: number; ry: number; }
interface AvatarLimb { path: string; width: number; }
export interface SpatialPosePresentation {
  pose: 'standing' | 'sitting' | 'laying' | 'unknown';
  label: string;
  color: string;
  edge: string;
  eventLabel: string;
  ellipses: readonly AvatarEllipse[];
  limbs: readonly AvatarLimb[];
  bodyPath: string;
  headPath: string;
  shadow: { rx: number; ry: number; };
}

// Port of sensor_union_pc/avatar_renderer.py, flat_2d (the supplied YAML uses size_px: 64).
// Local x is lateral, local -y is forward; the SVG origin remains the BE map position.
const SIZE = 64;
const point = (lateral: number, longitudinal: number): string => `${lateral * SIZE},${-longitudinal * SIZE}`;
function polygon(points: readonly (readonly [number, number])[]): string {
  return 'M' + points.map(([x, y]) => point(x, y)).join('L') + 'Z';
}
function head(longitudinal: number, radius: number): string {
  return polygon([
    [-.22, 1], [-.58, .86], [-.8, .52], [-.84, .08], [-.7, -.38], [-.44, -.76], [-.2, -.96],
    [.2, -.96], [.44, -.76], [.7, -.38], [.84, .08], [.8, .52], [.58, .86], [.22, 1]
  ].map(([x, y]) => [x * radius, longitudinal + y * radius]));
}
function ellipse(x: number, y: number, rx: number, ry: number): AvatarEllipse {
  return { cx: x * SIZE, cy: -y * SIZE, rx: Math.max(2, rx * SIZE), ry: Math.max(2, ry * SIZE) };
}
const geometry = {
  standing: {
    ellipses: [ellipse(-.105, .225, .034, .082), ellipse(.105, .225, .034, .082), ellipse(0, .035, .255, .165),
      ellipse(-.270, .020, .038, .080), ellipse(.270, .020, .038, .080)],
    limbs: [], bodyPath: '', headPath: head(-.035, .135), shadow: { rx: SIZE * .12, ry: SIZE * .23 }
  },
  sitting: {
    ellipses: [],
    limbs: [-1, 1].flatMap(side => [
      { path: `M${point(side * .075, -.08)}L${point(side * .19, -.13)}L${point(side * .18, -.31)}`, width: 5 },
      { path: `M${point(side * .14, .10)}L${point(side * .17, -.01)}L${point(side * .13, -.12)}`, width: 4 }
    ]),
    bodyPath: polygon([[-.13, -.11], [-.16, .10], [-.065, .19], [.065, .19], [.16, .10], [.13, -.11]]),
    headPath: head(.260, .072), shadow: { rx: SIZE * .18, ry: SIZE * .25 }
  },
  laying: {
    ellipses: [], limbs: [],
    bodyPath: polygon([
      [-.050, .300], [-.140, .260], [-.185, .165], [-.225, -.030], [-.205, -.090], [-.155, -.075],
      [-.125, .075], [-.090, .015], [-.110, -.075], [-.105, -.425], [-.090, -.475], [-.025, -.475],
      [-.020, -.125], [0, -.075], [.020, -.125], [.025, -.475], [.090, -.475], [.105, -.425],
      [.110, -.075], [.090, .015], [.125, .075], [.155, -.075], [.205, -.090], [.225, -.030],
      [.185, .165], [.140, .260], [.050, .300]
    ]),
    headPath: head(.370, .072), shadow: { rx: SIZE * .17, ry: SIZE * .43 }
  },
  unknown: { ellipses: [], limbs: [], bodyPath: '', headPath: '', shadow: { rx: 0, ry: 0 } }
};
const events = {
  normal: { color: '#68c25c', edge: '#407839', eventLabel: '' },
  fall: { color: '#e84c4c', edge: '#902f2f', eventLabel: 'Caduta' },
  bed_exit: { color: '#ecd648', edge: '#92852d', eventLabel: 'Uscita dal letto' },
  optical_flow: { color: '#d4d3cf', edge: '#838380', eventLabel: 'Osservazione Optical Flow' }
};

/** Pose confidence is separate from detection confidence (which is zero for held tracks). */
export function spatialPosePresentation(person: Pick<SpatialPerson, 'pose' | 'pose_confidence' | 'event_state'>): SpatialPosePresentation {
  const event = person.event_state;
  const colors = event === 'fall' ? events.fall : event === 'bed_exit' ? events.bed_exit
    : event === 'optical_flow' ? events.optical_flow : events.normal;
  const uncertain = person.pose_confidence != null && (!Number.isFinite(person.pose_confidence) || person.pose_confidence < .7);
  const pose = !uncertain && event !== 'optical_flow' && (person.pose === 'standing' || person.pose === 'sitting' || person.pose === 'laying')
    ? person.pose : 'unknown';
  const label = pose === 'standing' ? 'In piedi' : pose === 'sitting' ? 'Seduta' : pose === 'laying' ? 'Sdraiata'
    : uncertain ? 'Posa incerta' : 'Posa non disponibile';
  return { pose, label, ...colors, ...geometry[pose] };
}

/** Project only the velocity direction; marker coordinates still come directly from map_position_px. */
export function spatialAvatarHeading(person: SpatialPerson, matrix: SpatialMatrix3x3): number | null {
  const velocity = person.velocity_xz_m_s;
  if (person.state === 'held' || !Array.isArray(velocity) || velocity.length !== 2 || !velocity.every(Number.isFinite)
    || Math.hypot(...velocity) < .05) return null;
  const project = (x: number, z: number): [number, number] => {
    const w = matrix[2][0] * x + matrix[2][1] * z + matrix[2][2];
    return [(matrix[0][0] * x + matrix[0][1] * z + matrix[0][2]) / w,
      (matrix[1][0] * x + matrix[1][1] * z + matrix[1][2]) / w];
  };
  const start = project(person.x_m, person.z_m), end = project(person.x_m + velocity[0], person.z_m + velocity[1]);
  const dx = end[0] - start[0], dy = end[1] - start[1];
  return Number.isFinite(dx) && Number.isFinite(dy) && Math.hypot(dx, dy) > 1e-6 ? Math.atan2(dy, dx) * 180 / Math.PI + 90 : null;
}
